import { Badge, type BadgeProps } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

export type StatusType =
  | 'active'
  | 'inactive'
  | 'pending'
  | 'disabled'
  | 'completed'
  | 'cancelled'
  | 'error'
  | 'warning'
  | 'success'
  | 'info'
  | 'new'
  | 'in_progress'
  | 'resolved'

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  status: StatusType
  label?: string
  showDot?: boolean
}

const statusConfig: Record<
  StatusType,
  { variant: BadgeProps['variant']; labelKey: string; dotColor: string }
> = {
  active: { variant: 'success', labelKey: 'components.status_badge.active', dotColor: 'bg-success-500' },
  inactive: { variant: 'gray', labelKey: 'components.status_badge.inactive', dotColor: 'bg-gray-400' },
  pending: { variant: 'warning', labelKey: 'components.status_badge.pending', dotColor: 'bg-warning-500' },
  disabled: { variant: 'gray', labelKey: 'components.status_badge.disabled', dotColor: 'bg-gray-400' },
  completed: { variant: 'success', labelKey: 'components.status_badge.completed', dotColor: 'bg-success-500' },
  cancelled: { variant: 'gray', labelKey: 'components.status_badge.cancelled', dotColor: 'bg-gray-400' },
  error: { variant: 'error', labelKey: 'components.status_badge.error', dotColor: 'bg-error-500' },
  warning: { variant: 'warning', labelKey: 'components.status_badge.warning', dotColor: 'bg-warning-500' },
  success: { variant: 'success', labelKey: 'components.status_badge.success', dotColor: 'bg-success-500' },
  info: { variant: 'brand', labelKey: 'components.status_badge.info', dotColor: 'bg-brand-500' },
  new: { variant: 'brand', labelKey: 'components.status_badge.new', dotColor: 'bg-brand-500' },
  in_progress: { variant: 'warning', labelKey: 'components.status_badge.in_progress', dotColor: 'bg-warning-500' },
  resolved: { variant: 'success', labelKey: 'components.status_badge.resolved', dotColor: 'bg-success-500' },
}

export function StatusBadge({
  status,
  label,
  showDot = true,
  className,
  ...props
}: StatusBadgeProps) {
  const { t } = useTranslation()
  const config = statusConfig[status]
  const displayLabel = label || t(config.labelKey)

  return (
    <Badge variant={config.variant} className={cn('gap-1.5', className)} {...props}>
      {showDot && (
        <span className={cn('h-1.5 w-1.5 rounded-full', config.dotColor)} />
      )}
      {displayLabel}
    </Badge>
  )
}

export default StatusBadge
